import { fitWithin } from '@/lib/checklists';

export interface CompressedImage {
  blob: Blob;
  width: number;
  height: number;
}

/** Longest side for job photos: sharp on a phone, small enough to upload on 4G. */
export const JOB_PHOTO_MAX_SIDE = 1600;
export const JOB_PHOTO_QUALITY = 0.8;

async function loadBitmap(file: Blob): Promise<{ source: CanvasImageSource; width: number; height: number; close: () => void }> {
  if (typeof createImageBitmap === 'function') {
    try {
      // imageOrientation honours EXIF rotation from phone cameras.
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions);
      return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() };
    } catch {
      // Fall through to <img> (e.g. older Safari).
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('That file isn’t an image we can read.'));
      el.src = url;
    });
    return { source: img, width: img.naturalWidth, height: img.naturalHeight, close: () => URL.revokeObjectURL(url) };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
}

/**
 * Shrink a photo in the browser before it's uploaded: longest side at most
 * maxSide, re-encoded as JPEG. Falls back to the original file if the browser
 * can't draw it.
 */
export async function compressImage(
  file: Blob,
  maxSide = JOB_PHOTO_MAX_SIDE,
  quality = JOB_PHOTO_QUALITY,
): Promise<CompressedImage> {
  const image = await loadBitmap(file);
  try {
    const size = fitWithin(image.width, image.height, maxSide);
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return { blob: file, width: image.width, height: image.height };
    ctx.drawImage(image.source, 0, 0, size.width, size.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob) return { blob: file, width: image.width, height: image.height };
    return { blob, ...size };
  } finally {
    image.close();
  }
}
